import React, { useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { TrackingClient } from '@sparefoot/tracking-client';
import { PAGE_SUB_TYPES } from '@sparefoot/tracking-events';

import FacilityCard from 'components/organisms/FacilityCard/FacilityCard';
import DealsFacilityCard from 'components/organisms/DealsFacilityCard/DealsFacilityCard';
import {
	highlightFacility as highlightFacilityAction,
	clearHighlightedFacility as clearHighlightedFacilityAction,
} from 'store/search/slice';

import './FacilityList.scss';

export function FacilityList({
	facilities = [],
	showNoResults = false,
	baseUrl = '',
	searchParams = {},
	storageType = 'storage',
	withSchema = false,
	highlightedFacility,
	vipFacilityId,
	firstListingIsVipEligible,
	searchTerm,
	pageType,
	match
}) {
	const dispatch = useDispatch();

	const handleHighlightFacility = useCallback(
		(facilityId) => dispatch(highlightFacilityAction(facilityId)),
		[dispatch],
	);

	const handleClearHighlightedFacility = useCallback(
		() => dispatch(clearHighlightedFacilityAction()),
		[dispatch],
	);

	// Track facility list views on mount and when facilities change
	const prevFacilityIdsRef = useRef(null);

	useEffect(() => {
		if (!facilities.length) return;

		// Build facility IDs string to compare
		const currentFacilityIds = facilities.map((f) => f.id).join(',');

		// Skip if same facilities (prevents duplicate tracking on unrelated re-renders)
		if (prevFacilityIdsRef.current === currentFacilityIds) return;
		prevFacilityIdsRef.current = currentFacilityIds;

		const facilityIds = [];
		const formattedFacilities = facilities.map((facility) => {
			facilityIds.push(facility?.id);
			return {
				id: facility?.id,
				name: facility?.name,
				location: facility?.location?.id,
				facility_rank: facility?.facilityRank || '',
			};
		});

		TrackingClient.trackFacilityListViewed({
			facilities: formattedFacilities,
			facilities_list: facilityIds,
			search_term: searchTerm,
		});
	}, [facilities]);

	if (!facilities.length && showNoResults) {
		return (
			<div className="organisms-facility-list">
				<div className="facility-list facility-list--empty">
					No facilities available.
				</div>
			</div>
		);
	}

	if (!facilities.length) {
		return null;
	}

	const isDealsPage =
		pageType === PAGE_SUB_TYPES.STORAGE_DEALS_LANDING ||
		(pageType === PAGE_SUB_TYPES.STORAGE_NEAR_ME_LANDING &&
			match?.params?.keyword === 'storage-deals');

	return (
		<div className="organisms-facility-list">
			<div className="facility-list">
				{facilities.map((facility) => (
					<div
						key={facility.id}
						className="facility-list-card"
						id={facility.id ? `facility-${facility.id}` : undefined}
					>
						{isDealsPage ? (
							<DealsFacilityCard
								facility={facility}
								baseUrl={baseUrl}
								hasOMI={facility.hasOnlineMoveins}
								withSchema={withSchema}
								handleFacilitySelected={handleHighlightFacility}
								handleFacilityDeselected={handleClearHighlightedFacility}
								isHighlighted={facility.id === highlightedFacility}
								isVipFacility={
									facility.id === vipFacilityId &&
									firstListingIsVipEligible
								}
								dealBannerText={facility?.promotion || facility?.units?.[0]?.promotion || undefined}
							/>
						) : (
							<FacilityCard
								facility={facility}
								baseUrl={baseUrl}
								searchParams={searchParams}
								storageType={storageType}
								hasOMI={facility.hasOnlineMoveins}
								withSchema={withSchema}
								handleFacilitySelected={handleHighlightFacility}
								handleFacilityDeselected={handleClearHighlightedFacility}
								isHighlighted={facility.id === highlightedFacility}
								isVipFacility={
									facility.id === vipFacilityId &&
									firstListingIsVipEligible
								}
							/>
						)}
					</div>
				))}
			</div>
		</div>
	);
}

FacilityList.propTypes = {
	facilities: PropTypes.arrayOf(PropTypes.object),
	showNoResults: PropTypes.bool,
	baseUrl: PropTypes.string,
	searchParams: PropTypes.object,
	storageType: PropTypes.string,
	withSchema: PropTypes.bool,
	hasOMI: PropTypes.bool,
	highlightFacility: PropTypes.func,
	clearHighlightedFacility: PropTypes.func,
	highlightedFacility: PropTypes.number,
	vipFacilityId: PropTypes.string,
	firstListingIsVipEligible: PropTypes.bool,
	searchTerm: PropTypes.string,
	pageType: PropTypes.string,
	match: PropTypes.object,
};

export default FacilityList;
